import React, { Component } from "react";
import { getUid } from "../../../utils/uid";
import { Minus, Plus } from "@vitalsource/vstui-icons";
import styled, { css } from "styled-components";
import { cloud } from "@vitalsource/vstui/colors";
import { clamp } from "../../../utils/number-utils";

const TRACK_SIZE = 2;
const THUMB_SIZE = 12;
const HALF_SPACING = 8;

const styles = {
  trackStyles: css`
    height: ${TRACK_SIZE}px;
    border-radius: ${TRACK_SIZE}px;
    background-color: rgba(0, 0, 0, 0.2);
  `,
  thumbStyles: css`
    appearance: none;
    height: ${THUMB_SIZE}px;
    width: ${THUMB_SIZE}px;
    border-radius: ${THUMB_SIZE}px;
    background: white;
    box-shadow: 0 0 0 0.5px rgba(0, 0, 0, 0.1),
      0 0.5px 1px 0 rgba(0, 0, 0, 0.27);
  `
};

const Styled = {
  RangeInput: styled.input`
    appearance: none;
    padding: 0 ${HALF_SPACING}px;
    width: 100%;
    height: 28px;
    margin: 0;
    border: 0;
    cursor: pointer;
    &:focus {
      border: 0;
      border-radius: 2px;
      box-shadow: 0 0 0 2px
        ${props => props.theme.vstui.color.primary.getShade(6)};
    }
    /* These get cranky if you use shared declarations, so reusing vars instead */
    &::-webkit-slider-runnable-track {
      ${styles.trackStyles}
    }
    &::-moz-range-track {
      ${styles.trackStyles}
    }
    &::-ms-track {
      height: ${TRACK_SIZE}px;
      background: transparent;
      border-color: transparent;
      color: transparent;
    }
    &::-ms-fill-lower {
      ${styles.trackStyles}
    }
    &::-ms-fill-upper {
      ${styles.trackStyles}
    }
    &::-webkit-slider-thumb {
      margin-top: -${THUMB_SIZE / 2 - TRACK_SIZE / 2}px;
      ${styles.thumbStyles}
    }
    &::-moz-range-thumb {
      ${styles.thumbStyles}
    }
    &::-ms-thumb {
      ${styles.thumbStyles}
    }
  `,
  OuterContainer: styled.div`
    padding-bottom: 10px;
  `,
  SliderLabel: styled.label`
    font-weight: 500;
    font-size: 1.3rem;
    display: block;
    margin-bottom: 7px;
  `,
  RangeContainer: styled.div`
    display: flex;
    align-items: center;
  `,
  IndicatorContainer: styled.div`
    color: ${cloud.getShade(9)};
    display: flex;
    align-items: center;
    &:first-child {
      margin-right: ${HALF_SPACING}px;
    }
    &:last-child > * {
      margin-left: ${HALF_SPACING}px;
    }
  `,
  IndicatorButton: styled.button`
    margin: 0;
    padding: 0;
    border: 0;
    background: none;
    display: flex;
    align-items: center;
    height: 20px;
    &:focus {
      outline: 0;
      border-radius: 2px;
      box-shadow: rgb(255, 255, 255) 0 0 0 2px,
        ${props => props.theme.vstui.color.primary.getShade(6)} 0 0 0 4px;
    }
  `
};

const BUTTON_STEP = 0.1;

function Indicator({ children, onClick, label }) {
  return (
    <Styled.IndicatorContainer>
      <Styled.IndicatorButton
        onClick={onClick}
        type="button"
        aria-label={label}
      >
        {children}
      </Styled.IndicatorButton>
    </Styled.IndicatorContainer>
  );
}

export default class Slider extends Component {
  inputId = getUid("Slider");

  sendFakeEvent(delta) {
    const numVal = parseFloat(this.props.value);
    const value = clamp(numVal + delta, 0, 1);
    this.props.onChange({
      currentTarget: {
        value
      }
    });
  }

  handleRangeIncrease = () => {
    this.sendFakeEvent(BUTTON_STEP);
  };

  handleRangeDecrease = () => {
    this.sendFakeEvent(-BUTTON_STEP);
  };

  render() {
    return (
      <Styled.OuterContainer>
        <Styled.SliderLabel htmlFor={this.inputId}>
          {i18n.t("tts.reading-speed")}
        </Styled.SliderLabel>
        <Styled.RangeContainer>
          <Indicator
            onClick={this.handleRangeDecrease}
            label={i18n.t("tts.decrease-rate")}
          >
            <Minus />
          </Indicator>
          <Styled.RangeInput
            id={this.inputId}
            type="range"
            step={0.1}
            onChange={this.props.onChange}
            min={0}
            max={1}
            value={this.props.value}
          />
          <Indicator
            onClick={this.handleRangeIncrease}
            label={i18n.t("tts.increase-rate")}
          >
            <Plus />
          </Indicator>
        </Styled.RangeContainer>
      </Styled.OuterContainer>
    );
  }
}
